import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  SearchIcon,
  MotorcycleIcon,
  HashIcon,
  ChevronRightIcon,
} from "../lib/icons.js";
import { Input } from "../ui/Input.js";
import { Button } from "../ui/Button.js";

/**
 * Home — vstupní bod. Dvoudomeček: hledání modelem | hledání dílem.
 * Nad ním rychlý global search, kam jde napsat cokoliv.
 */
export function HomePage(): JSX.Element {
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  function handleGlobalSearch(e: React.FormEvent): void {
    e.preventDefault();
    const trimmed = q.trim();
    if (!trimmed) return;
    // Heuristika: pokud číslice/řetězec podobný article number → part search
    const looksLikeArticleNumber = /^[\w.-]{5,}$/.test(trimmed) && /\d/.test(trimmed);
    const target = looksLikeArticleNumber
      ? `/search/part?q=${encodeURIComponent(trimmed)}`
      : `/search/model?q=${encodeURIComponent(trimmed)}`;
    navigate(target);
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 text-balance">
          Najděte díl pro vaše KTM
        </h1>
        <p className="mt-3 text-zinc-500 text-lg">
          Interní katalog přes oficiální data výrobce. 2 036 modelů, 626 000 dílů.
        </p>
      </div>

      <form onSubmit={handleGlobalSearch} className="max-w-2xl mx-auto mb-14">
        <Input
          sizeVariant="lg"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Model, číslo dílu nebo název…"
          iconLeft={<SearchIcon size={20} />}
          aria-label="Globální vyhledávání"
        />
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ChoiceCard
          to="/search/model"
          icon={<MotorcycleIcon size={28} />}
          title="Hledám díl k modelu"
          description="Zvolím motorku, projdu skupiny a výkresy a kliknu na konkrétní díl."
          action="Vybrat model"
        />
        <ChoiceCard
          to="/search/part"
          icon={<HashIcon size={28} />}
          title="Hledám konkrétní díl"
          description="Zadám číslo dílu z dokladu nebo servisního manuálu a zjistím, kam patří."
          action="Zadat číslo"
        />
      </div>

      <RecentlyViewed />
    </div>
  );
}

function ChoiceCard({
  to,
  icon,
  title,
  description,
  action,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  action: string;
}): JSX.Element {
  return (
    <Link
      to={to}
      className="group relative bg-white rounded-2xl border border-zinc-200 shadow-card hover:shadow-card-hover hover:border-ktm-orange-100 transition-all duration-200 p-8 flex flex-col overflow-hidden"
    >
      <div className="w-14 h-14 rounded-xl bg-ktm-orange-50 text-ktm-orange-600 flex items-center justify-center mb-6 group-hover:bg-ktm-orange group-hover:text-white transition-colors">
        {icon}
      </div>
      <h2 className="text-xl font-semibold text-zinc-900">{title}</h2>
      <p className="mt-2 text-sm text-zinc-500 flex-1">{description}</p>
      <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-ktm-orange group-hover:gap-2.5 transition-all">
        {action}
        <ChevronRightIcon size={16} />
      </div>
    </Link>
  );
}

function RecentlyViewed(): JSX.Element | null {
  const items = readRecent();
  if (items.length === 0) return null;
  return (
    <div className="mt-16">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide">
          Naposledy prohlížené
        </h3>
        <Button
          variant="link"
          onClick={() => {
            localStorage.removeItem("ktm.recent");
            window.location.reload();
          }}
        >
          Vymazat
        </Button>
      </div>
      <ul className="divide-y divide-zinc-100 bg-white rounded-xl border border-zinc-200 shadow-card overflow-hidden">
        {items.map((item) => (
          <li key={item.url}>
            <Link
              to={item.url}
              className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors"
            >
              <div className="w-2 h-2 rounded-full bg-ktm-orange" />
              <div className="flex-1 text-sm text-zinc-900">{item.label}</div>
              <div className="text-xs text-zinc-400">{item.subtitle}</div>
              <ChevronRightIcon size={16} className="text-zinc-300" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface RecentItem {
  url: string;
  label: string;
  subtitle: string;
  at: number;
}

function readRecent(): RecentItem[] {
  try {
    const raw = localStorage.getItem("ktm.recent");
    if (!raw) return [];
    return JSON.parse(raw) as RecentItem[];
  } catch {
    return [];
  }
}

export function pushRecent(item: Omit<RecentItem, "at">): void {
  try {
    const current = readRecent();
    const filtered = current.filter((it) => it.url !== item.url);
    const next = [{ ...item, at: Date.now() }, ...filtered].slice(0, 5);
    localStorage.setItem("ktm.recent", JSON.stringify(next));
  } catch {
    // ignore
  }
}
